import { useState, useEffect, useCallback, useRef } from 'react';
import { corelCommunication } from '../lib/corel/communication';
import { sendToTerminal } from '../lib/terminalLogger';
import { useNotifications } from '../components/feedback/NotificationProvider';
import type { WebViewResponse } from '../lib/corel/types/messages';
import { LogCategory, getLogLevel, shouldLogConnectionDetails } from '../config/logging';
import { isPipelineRunning } from '../lib/corel/pipeline/orderPreparation';
import { LONG_RUNNING_ACTIONS } from '../lib/corel/constants';

type ConnectionStatus = 'healthy' | 'validating' | 'disconnected' | 'idle' | 'degraded';

interface UseCorelConnectionReturn {
  status: ConnectionStatus;
  isValidating: boolean;
  error: string | null;
  latencyMs: number | null;
  lastCheck: number | null;
  validate: () => Promise<void>;
  retry: () => void;
}

const VALIDATION_TIMEOUT_MS = 5000;
const INITIAL_VALIDATION_TIMEOUT_MS = 1500; // Primeiro check rápido (não bloqueia login)
const RETRY_DELAY_MS = 15000; // 15s para app-status conforme recomendação
const MAX_RETRY_ATTEMPTS = 3;
const NOTIFICATION_STORAGE_KEY = 'corel_connection_notified';

/**
 * Helper para verificar se há operações longas em andamento
 * 
 * Verifica:
 * 1. Pipeline de preparação rodando
 * 2. Ações longas pendentes no RequestTracker
 */
function hasLongRunningOperation(): boolean {
  // 1. Verificar pipeline de preparação
  if (isPipelineRunning()) {
    return true;
  }
  
  // 2. Verificar ações longas pendentes
  const pendingRequests = corelCommunication.getPendingRequestsInfo();
  const hasLongAction = pendingRequests.some(
    req => req.action && LONG_RUNNING_ACTIONS.has(req.action)
  );
  
  return hasLongAction;
}

export function useCorelConnection(): UseCorelConnectionReturn {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [lastCheck, setLastCheck] = useState<number | null>(null);
  
  const { notify } = useNotifications();
  
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const firstAttemptTimeRef = useRef<number | null>(null);
  const lastAttemptTimeRef = useRef<number | null>(null);
  const isFirstSuccessRef = useRef(true);
  const isMountedRef = useRef(true);
  
  // Contador de notificações (limitar a 1-2)
  const notificationCountRef = useRef(0);
  const lastNotificationTimeRef = useRef<number | null>(null);

  const validate = useCallback(async (isManualRetry = false) => {
    if (!isMountedRef.current) return;

    const attemptNumber = retryCountRef.current + 1;
    const startTime = Date.now();

    // ✅ NOVO: Verificar operações longas antes de validar
    if (hasLongRunningOperation()) {
      const level = getLogLevel();
      if (shouldLogConnectionDetails(level)) {
        const pendingRequests = corelCommunication.getPendingRequestsInfo();
        sendToTerminal('⏸️ [COREL_CONNECTION] Validação pausada - operação longa em andamento', {
          pendingActions: pendingRequests.map(r => r.action).filter(Boolean),
          isPipelineRunning: isPipelineRunning(),
          attemptNumber
        }, LogCategory.CONNECTION);
      }
      
      // Manter status atual (não mudar para disconnected)
      // Agendar próxima validação em 10s
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = window.setTimeout(() => {
        if (isMountedRef.current) {
          validate(false);
        }
      }, 10000); // 10s durante operações longas
      
      return; // Não validar durante operações longas
    }

    // Verificar se WebView2 está disponível
    if (!window.chrome?.webview) {
      const level = getLogLevel();
      // Apenas nível 3 loga detalhes de conexão, mas erros sempre são logados
      if (shouldLogConnectionDetails(level)) {
        sendToTerminal('⚠️ [COREL_CONNECTION] WEBVIEW_UNAVAILABLE', {
          status: 'idle',
          environment: process.env.NODE_ENV,
          timestamp: Date.now()
        }, LogCategory.CONNECTION);
      }
      setStatus('idle');
      return;
    }

    // ✅ MUDANÇA: Verificar comunicação ativa primeiro (via lastResponseAt)
    // O token correto (64 hex) pode chegar depois nas respostas, mas a comunicação já está ativa
    const lastResponseAt = corelCommunication.getLastResponseAt();
    const hasRecentResponse = lastResponseAt && (Date.now() - lastResponseAt) < 40000; // 40s
    
    if (hasRecentResponse) {
      // Comunicação ativa detectada - considerar conectado mesmo sem token válido ainda
      const level = getLogLevel();
      if (shouldLogConnectionDetails(level)) {
        sendToTerminal('✅ [COREL_CONNECTION] Comunicação ativa detectada', {
          lastResponseAt,
          deltaMs: Date.now() - lastResponseAt!,
          note: 'Token correto pode chegar depois, mas comunicação está funcionando'
        }, LogCategory.CONNECTION);
      }
      
      setStatus('healthy');
      setError(null);
      setLastCheck(Date.now());
      retryCountRef.current = 0;
      
      // Agendar próxima validação em 15s (ritmo do app-status)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = window.setTimeout(() => {
        if (isMountedRef.current) {
          validate(false);
        }
      }, 15000);
      
      return;
    }

    // ✅ MUDANÇA: app-status é isenta de token conforme guia do backend
    // Enviar app-status mesmo sem token válido e usar a resposta como fonte da verdade
    // O token correto (64 hex) pode chegar depois nas respostas

    if (!firstAttemptTimeRef.current) {
      firstAttemptTimeRef.current = startTime;
    }
    lastAttemptTimeRef.current = startTime;

    // Log: Início da validação (apenas nível 3)
    const level = getLogLevel();
    if (shouldLogConnectionDetails(level)) {
      sendToTerminal('🔍 [COREL_CONNECTION] VALIDATION_START', {
        timestamp: startTime,
        attemptNumber,
        maxAttempts: MAX_RETRY_ATTEMPTS,
        isManualRetry
      }, LogCategory.CONNECTION);
    }

    // No primeiro check (mount), NÃO mudar para 'validating' — manter 'idle'
    // para que a UI renderize imediatamente. Checks subsequentes mostram spinner.
    const isFirstCheck = isFirstSuccessRef.current && !isManualRetry;
    if (!isFirstCheck) {
      setStatus('validating');
    }
    setError(null);

    // Timeout menor no primeiro check (1.5s) para não travar a tela de login
    const effectiveTimeout = isFirstCheck ? INITIAL_VALIDATION_TIMEOUT_MS : VALIDATION_TIMEOUT_MS;

    let timeoutId: number | null = null;
    try {
      // Criar promise de timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error('Timeout'));
        }, effectiveTimeout);
      });

      // Criar promise de resposta
      const responsePromise = new Promise<WebViewResponse>((resolve, reject) => {
        const requestId = corelCommunication.sendActionGuarded(
          'app-status',
          {
            apiOnline: true,
            appVersion: '1.0.0',
            timestamp: new Date().toISOString()
          },
          (response: WebViewResponse) => {
            if (!isMountedRef.current) return;
            if (timeoutId) clearTimeout(timeoutId);
            resolve(response);
          }
        );

        // Log: Envio do app-status (sempre logar para diagnóstico)
        sendToTerminal('📤 [COREL_CONNECTION] APP_STATUS_SENT', {
          requestId: requestId || 'null',
          timeoutMs: VALIDATION_TIMEOUT_MS,
          wasBlocked: requestId === null,
          timestamp: Date.now()
        }, requestId ? LogCategory.CONNECTION : LogCategory.ERROR);
        
        // Se foi bloqueado, rejeitar a promise
        if (!requestId) {
          reject(new Error('app-status foi bloqueado pelo guard (verificar logs de GUARD)'));
        }
      });

      // Aguardar resposta ou timeout
      const response = await Promise.race([responsePromise, timeoutPromise]);

      if (!isMountedRef.current) return;

      const roundTripMs = Date.now() - startTime;

      // Log: Resposta recebida (sempre logar para diagnóstico)
      sendToTerminal('📥 [COREL_CONNECTION] APP_STATUS_RESPONSE', {
        requestId: response.requestId,
        responseStatus: response.status,
        communicationStatus: response.result?.communication?.status,
        deserializationSuccessful: response.result?.communication?.deserializationSuccessful,
        corelStatus: response.result?.system?.corelStatus,
        roundTripMs: response.result?.communication?.roundTripTimeMs || roundTripMs,
        allFieldsReceived: response.result?.communication?.allFieldsReceived,
        echoAction: response.result?.echo?.requestReceived,
        timestamp: Date.now()
      }, LogCategory.CONNECTION);

      // Verificar se resposta indica erro
      if (response.status === 'erro') {
        const msg = response.message || '';
        const resultStr = JSON.stringify(response.result ?? {});
        
        // ✅ DEBUG: Detectar erro de launcher não pronto
        const isLauncherNotReady = msg.includes('launcher_session_required') || 
                                   msg.includes('booting') ||
                                   resultStr.includes('"launcherState":"booting"');
        
        // Log detalhado do erro de app-status para diagnóstico
        sendToTerminal(isLauncherNotReady ? '🚨 [COREL_CONNECTION] LAUNCHER_NAO_PRONTO' : '❌ [COREL_CONNECTION] APP_STATUS_ERROR', {
          requestId: response.requestId,
          message: response.message,
          resultPreview: resultStr.substring(0, 200),
          isLauncherNotReady,
          launcherState: response.result?.launcherState || 'unknown',
          roundTripMs,
          sessionTokenPresent: !!(window as any)?.sessionToken
        }, LogCategory.ERROR);
        throw new Error(response.message || 'Erro desconhecido');
      }

      // Verificar deserialização
      if (response.result?.communication?.status === 'degraded') {
        // Erros sempre são logados (categoria ERROR)
        sendToTerminal('❌ [COREL_CONNECTION] DESERIALIZATION_FAILED', {
          communicationStatus: response.result.communication.status,
          allFieldsReceived: response.result.communication.allFieldsReceived,
          deserializationSuccessful: response.result.communication.deserializationSuccessful,
          echoAction: response.result.echo?.requestReceived,
          willRetry: false,
          timestamp: Date.now(),
          requestId: response.requestId,
          resultPreview: JSON.stringify(response.result ?? {}).substring(0, 200)
        }, LogCategory.ERROR);

        setStatus('degraded');
        setError('Comunicação degradada');
        
        notify({
          type: 'warning',
          title: 'Comunicação Degradada',
          message: 'Problema detectado na comunicação com CorelDRAW',
          sticky: false
        });

        return; // Não fazer retry em erro de deserialização
      }

      // ✅ MUDANÇA: Usar result.system.corelStatus como fonte da verdade (conforme guia)
      const corelStatus = response.result?.system?.corelStatus;
      const commStatus = response.result?.communication?.status;
      
      // Verificar se CorelDRAW está conectado (campo crítico conforme guia)
      if (corelStatus !== 'connected') {
        // CorelDRAW não está conectado ou status desconhecido
        const errorMsg = corelStatus === 'disconnected' 
          ? 'CorelDRAW não está aberto'
          : 'CorelDRAW não está conectado';
          
        sendToTerminal('🔌 [COREL_CONNECTION] COREL_NOT_CONNECTED', {
          corelStatus: corelStatus || 'null',
          commStatus: commStatus || 'null',
          attemptNumber,
          willRetry: retryCountRef.current < MAX_RETRY_ATTEMPTS - 1,
          nextRetryInMs: retryCountRef.current < MAX_RETRY_ATTEMPTS - 1 ? RETRY_DELAY_MS : null,
          timestamp: Date.now()
        }, LogCategory.ERROR);

        setStatus('disconnected');
        setError(errorMsg);

        notify({
          type: 'info',
          title: 'CorelDRAW não encontrado',
          message: 'Verifique se o CorelDRAW está aberto',
          sticky: false
        });

        scheduleRetry();
        return;
      }

      // ✅ Sucesso: corelStatus === 'connected' (fonte da verdade conforme guia)
      const latency = response.result?.communication?.roundTripTimeMs || roundTripMs;
      
      // Log de sucesso (sempre logar para diagnóstico)
      sendToTerminal('✅ [COREL_CONNECTION] VALIDATION_SUCCESS', {
        latencyMs: latency,
        communicationHealth: 'healthy',
        corelStatus: response.result?.system?.corelStatus,
        corelVersion: response.result?.system?.corelVersion,
        documentOpen: response.result?.system?.documentOpen,
        buildMode: response.result?.app?.buildMode,
        appVersion: response.result?.app?.version,
        timestamp: Date.now()
      }, LogCategory.CONNECTION);

      // ✅ Status já verificado acima - corelStatus === 'connected'
      // Marcar como healthy (conforme guia: corelStatus === 'connected' é fonte da verdade)
      setStatus('healthy');
      setError(null);
      setLatencyMs(latency);
      setLastCheck(Date.now());
      retryCountRef.current = 0; // Reset contador

      // Notificar apenas na primeira conexão bem-sucedida após login real
      // Usa sessionStorage para garantir que aparece apenas uma vez por sessão
      if (isFirstSuccessRef.current) {
        // Verificar se já notificou nesta sessão
        const alreadyNotified = typeof sessionStorage !== 'undefined' 
          ? sessionStorage.getItem(NOTIFICATION_STORAGE_KEY) === 'true'
          : false;
        
        if (!alreadyNotified) {
          notify({
            type: 'success',
            title: 'Sistema Conectado',
            message: `Conexão estabelecida (${latency}ms)`,
            sticky: false,
            duration: 2500 // Toast mais rápido: 2.5s ao invés de 4s padrão
          });
          
          // Marcar como notificado nesta sessão
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(NOTIFICATION_STORAGE_KEY, 'true');
          }
        }
        
        isFirstSuccessRef.current = false;
      }
      
      // Notificação de reconexão manual removida - o indicador visual no footer já mostra o status

    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMessage = err instanceof Error ? err.message : String(err);
      const isTimeout = errorMessage === 'Timeout';

      // ✅ NOVO: Verificar operações longas antes de marcar como desconectado
      if (hasLongRunningOperation()) {
        sendToTerminal('⏸️ [COREL_CONNECTION] Timeout ignorado - operação longa em andamento', {
          timeoutMs: VALIDATION_TIMEOUT_MS,
          pendingActions: corelCommunication.getPendingRequestsInfo()
            .map(r => r.action)
            .filter(Boolean),
          isPipelineRunning: isPipelineRunning(),
          note: 'app-status pode não responder durante operações longas, mas operação está em andamento'
        }, LogCategory.CONNECTION);
        
        // Manter status atual e agendar nova validação
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = window.setTimeout(() => {
          if (isMountedRef.current) {
            validate(false);
          }
        }, 10000); // 10s durante operações longas
        
        return; // Não marcar como desconectado nem mostrar notificação
      }

      if (isTimeout) {
        // Erros sempre são logados (categoria ERROR)
        sendToTerminal('⏱️ [COREL_CONNECTION] TIMEOUT', {
          timeoutMs: VALIDATION_TIMEOUT_MS,
          attemptNumber,
          maxAttempts: MAX_RETRY_ATTEMPTS,
          willRetry: retryCountRef.current < MAX_RETRY_ATTEMPTS - 1,
          nextRetryInMs: retryCountRef.current < MAX_RETRY_ATTEMPTS - 1 ? RETRY_DELAY_MS : null,
          timestamp: Date.now()
        }, LogCategory.ERROR);

        setStatus('disconnected');
        setError('Timeout ao conectar');

        notify({
          type: 'warning',
          title: 'CorelDRAW não responde',
          message: 'Tentando reconectar automaticamente...',
          sticky: false
        });

        scheduleRetry();
      } else {
        // Erros sempre são logados (categoria ERROR)
        sendToTerminal('❌ [COREL_CONNECTION] ERROR_RESPONSE', {
          responseStatus: 'error',
          errorMessage,
          errorCode: null,
          attemptNumber,
          willRetry: retryCountRef.current < MAX_RETRY_ATTEMPTS - 1,
          timestamp: Date.now()
        }, LogCategory.ERROR);

        setStatus('disconnected');
        setError(errorMessage);

        scheduleRetry();
      }
    }
  }, [notify]);

  const scheduleRetry = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRY_ATTEMPTS - 1) {
      // ✅ NOVO: Verificar operações longas antes de notificar
      if (hasLongRunningOperation()) {
        sendToTerminal('⏸️ [COREL_CONNECTION] MAX_RETRIES ignorado - operação longa em andamento', {
          pendingActions: corelCommunication.getPendingRequestsInfo()
            .map(r => r.action)
            .filter(Boolean),
          isPipelineRunning: isPipelineRunning(),
          note: 'Não notificar durante operações longas'
        }, LogCategory.CONNECTION);
        
        // Agendar nova tentativa sem notificar
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = window.setTimeout(() => {
          if (isMountedRef.current) {
            retryCountRef.current = 0; // Reset contador
            validate(false);
          }
        }, 10000); // 10s durante operações longas
        
        return;
      }
      
      // Erros sempre são logados (categoria ERROR)
      sendToTerminal('🛑 [COREL_CONNECTION] MAX_RETRIES_REACHED', {
        totalAttempts: MAX_RETRY_ATTEMPTS,
        finalStatus: 'disconnected',
        lastError: error,
        totalDurationMs: firstAttemptTimeRef.current 
          ? Date.now() - firstAttemptTimeRef.current 
          : 0,
        timestamp: Date.now()
      }, LogCategory.ERROR);
      
      // ✅ MUDANÇA: Toast simples ao invés de modal sticky
      // Limitar notificações: máximo 2 vezes, com intervalo mínimo de 60s
      const now = Date.now();
      const timeSinceLastNotification = lastNotificationTimeRef.current 
        ? now - lastNotificationTimeRef.current 
        : Infinity;
      
      if (notificationCountRef.current < 2 && timeSinceLastNotification > 60000) {
        notificationCountRef.current += 1;
        lastNotificationTimeRef.current = now;
        
        // Toast não-bloqueante (sem sticky, sem modal)
        notify({
          type: 'warning', // Warning ao invés de error (menos alarmante)
          title: 'CorelDRAW não responde',
          message: 'Verifique se o CorelDRAW está aberto. O indicador no rodapé mostra o status.',
          sticky: false, // ✅ NÃO bloqueante
          duration: 5000 // 5 segundos e desaparece
        });
      }
      
      return;
    }

    retryCountRef.current += 1;

    // Log: Auto-retry iniciado (apenas nível 3)
    const level = getLogLevel();
    if (shouldLogConnectionDetails(level)) {
      sendToTerminal('🔁 [COREL_CONNECTION] AUTO_RETRY', {
        attemptNumber: retryCountRef.current + 1,
        maxAttempts: MAX_RETRY_ATTEMPTS,
        delayMs: RETRY_DELAY_MS,
        previousError: error,
        timeSinceLastAttempt: lastAttemptTimeRef.current 
          ? Date.now() - lastAttemptTimeRef.current 
          : 0,
        timestamp: Date.now()
      }, LogCategory.CONNECTION);
    }

    // Agendar próxima tentativa
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    retryTimeoutRef.current = window.setTimeout(() => {
      if (isMountedRef.current) {
        validate(false);
      }
    }, RETRY_DELAY_MS);
  }, [error, validate]);

  const retry = useCallback(() => {
    // Log: Retry manual (apenas nível 3)
    const level = getLogLevel();
    if (shouldLogConnectionDetails(level)) {
      sendToTerminal('🔄 [COREL_CONNECTION] MANUAL_RETRY', {
        previousStatus: status,
        previousError: error,
        retryCount: 0, // Reset contador
        timestamp: Date.now()
      }, LogCategory.CONNECTION);
    }

    // Limpar retry automático
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Reset contador para retry manual
    retryCountRef.current = 0;
    firstAttemptTimeRef.current = Date.now();

    // Executar validação
    validate(true);
  }, [status, error, validate]);

  // Iniciar validação após montagem (delay de 200ms para resposta mais rápida)
  useEffect(() => {
    isMountedRef.current = true;
    
    const initTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        validate(false);
      }
    }, 200);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initTimeout);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [validate]);

  // Validação periódica de app-status a cada 15s
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (isMountedRef.current) {
        validate(false);
      }
    }, RETRY_DELAY_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [validate]);

  // ✅ NOVO: Monitorar lastResponseAt para atualizar status imediatamente quando comunicação ativa
  useEffect(() => {
    const checkInterval = window.setInterval(() => {
      if (!isMountedRef.current) return;
      
      const lastResponseAt = corelCommunication.getLastResponseAt();
      if (lastResponseAt) {
        const deltaMs = Date.now() - lastResponseAt;
        
        // Se houver resposta recente (< 40s) e status não for healthy, atualizar
        if (deltaMs < 40000 && status !== 'healthy') {
          const level = getLogLevel();
          if (shouldLogConnectionDetails(level)) {
            sendToTerminal('✅ [COREL_CONNECTION] Status atualizado via lastResponseAt', {
              lastResponseAt,
              deltaMs,
              previousStatus: status
            }, LogCategory.CONNECTION);
          }
          
          setStatus('healthy');
          setError(null);
          setLastCheck(Date.now());
          retryCountRef.current = 0;
        }
        // Se não houver resposta recente (> 40s) e status for healthy, marcar como desconectado
        else if (deltaMs >= 40000 && status === 'healthy') {
          setStatus('disconnected');
          setError('Sem comunicação há mais de 40 segundos');
        }
      }
    }, 2000); // Verificar a cada 2s

    return () => {
      clearInterval(checkInterval);
    };
  }, [status]);

  return {
    status,
    isValidating: status === 'validating',
    error,
    latencyMs,
    lastCheck,
    validate,
    retry
  };
}

// Novo efeito para atualizar status com base na última resposta recebida (health ou qualquer action)
// Mantém verde se houve resposta <40s, caso contrário cinza/vermelho
export function useConnectionHeartbeat(): { status: ConnectionStatus; lastResponseAt: number | null } {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [heartbeatAt, setHeartbeatAt] = useState<number | null>(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const last = corelCommunication.getLastResponseAt();
      setHeartbeatAt(last);

      if (last) {
        const delta = Date.now() - last;
        if (delta < 40000) {
          setStatus('healthy');
        } else {
          setStatus('disconnected'); // Sem resposta >40s
        }
      } else {
        setStatus('idle'); // Ainda não recebeu nada
      }
    }, 5000); // checagem leve

    return () => clearInterval(interval);
  }, []);

  return { status, lastResponseAt: heartbeatAt };
}

